import { useState } from "react";
import { SEOHead } from "@components/organisms";
import { InnerPageLayout } from "@components/templates";
import { Button, InputField } from "@components/atoms";
import { SuccessModal } from "@components/molecules";
import { useAuth } from "@app/providers/useAuth";
import { Mail, MessageSquareText } from "lucide-react";

/**
 * Contact page.
 * Title "Contacto", subtitle, channel info (email + WhatsApp),
 * white bg input fields, "Enviar" button matching ProfilePage "Guardar".
 * Pre-fills from logged user.
 */
export function ContactPage() {
  const { user } = useAuth();

  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.mail ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [showSuccess, setShowSuccess] = useState(false);

  const canSubmit =
    name.trim() !== "" && email.trim() !== "" && phone.trim() !== "";

  const handleSubmit = () => {
    if (!canSubmit) return;

    // Build WhatsApp message with form data
    const message = `Hola, quería más información sobre TicketIn.

*Mis datos de contacto:*
• Nombre: ${name}
• Email: ${email}

Quedo atento a su respuesta. ¡Gracias!`;

    // Open WhatsApp in new tab
    const whatsappUrl = `https://wa.me/593983300690?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");

    setShowSuccess(true);
  };

  const handleClose = () => {
    setShowSuccess(false);
  };

  return (
    <>
      <SEOHead title="Contacto" />
      <InnerPageLayout title="Contacto">
        <div className="flex flex-col gap-6 px-4 sm:pl-8">
          {/* Subtitle */}
          <span className="font-semibold text-black" style={{ fontSize: 14 }}>
            Encuéntranos en nuestros canales directos:
          </span>

          {/* Channel info */}
          <div className="flex flex-col gap-4 px-4">
            {/* Email */}
            <div className="flex items-start gap-3 ">
              <Mail size={24} className="mt-0.5 text-black" />
              <div className="flex flex-col">
                <span
                  className="font-semibold text-black"
                  style={{ fontSize: 14 }}
                >
                  Email:
                </span>
                <span className="text-black" style={{ fontSize: 14 }}>
                  Escríbenos a info@ticketin.ec
                </span>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="flex items-start gap-3">
              <MessageSquareText size={24} className="mt-0.5 text-black" />
              <div className="flex flex-col">
                <span
                  className="font-semibold text-black"
                  style={{ fontSize: 14 }}
                >
                  WhatsApp:
                </span>
                <span className="text-black" style={{ fontSize: 14 }}>
                  Chatea con nosotros al +593 98-330-0690
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="flex w-full max-w-[665px] flex-col gap-4">
            <InputField
              label="Nombres y Apellidos"
              variant="editable"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <div className="flex flex-col gap-1">
              <InputField
                label="Correo Electrónico"
                variant="editable"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <span className="text-neutral-70" style={{ fontSize: 13 }}>
                Ingresa un correo electrónico válido
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <InputField
                label="Teléfono"
                variant="editable"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <span className="text-neutral-70" style={{ fontSize: 13 }}>
                Ingresa un número de teléfono válido
              </span>
            </div>
          </div>

          {/* Submit button — matches ProfilePage "Guardar" style */}
          <div className="mt-2 flex w-full max-w-[665px] mb-4">
            <Button
              className="w-full max-w-[340px] h-[50px] text-h5 bg-primary-110 text-white hover:bg-primary-110 hover:opacity-90 disabled:opacity-40"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              Enviar
            </Button>
          </div>
        </div>

        <SuccessModal
          isOpen={showSuccess}
          onClose={handleClose}
          title="¡Contacto enviado!"
          message="Hemos enviado tu contacto exitosamente, uno de nuestros asesores se contactará contigo para brindarte información."
        />
      </InnerPageLayout>
    </>
  );
}
